/**
 * Arrière-plan de la caméra : flou (retour #45) ou décor, réglage
 * `settings.videoBackground` (lib/videoBackgrounds.ts).
 * @livekit/track-processors détoure la personne (MediaPipe, dans le navigateur)
 * et remplace le reste avant l'envoi : les autres ne reçoivent que l'image
 * composée. Librairie chargée seulement quand un effet sert. Le modèle
 * MediaPipe vient des CDN par défaut de la librairie (jsdelivr, Google) au
 * premier usage.
 */
import { Track, type LocalVideoTrack, type Room } from 'livekit-client';
import type { BackgroundProcessorWrapper, SwitchBackgroundProcessorOptions } from '@livekit/track-processors';
import { EventBus, useGameStore } from '@campus/core';
import { backgroundPhotoUrl, isVideoBackground, loadCustomBackground } from '../lib/videoBackgrounds.js';

// Rayon en pixels de l'image d'origine. La librairie floute une image réduite
// au quart et plafonne à 16 points de cette image : 64 = le flou le plus fort.
// À 12 (premier essai), on voyait encore tout le décor derrière soi.
const BLUR_RADIUS = 64;
const LIGHT_BLUR_RADIUS = 20;
type Lib = typeof import('@livekit/track-processors');
let lib: Promise<Lib> | null = null;
let unsupportedWarned = false;

const loadLib = () => (lib ??= import('@livekit/track-processors'));

// Effet appliqué à chaque piste (clé du réglage + image perso), pour ne
// relancer la librairie que quand il change.
const applied = new WeakMap<LocalVideoTrack, string>();

/** Navigateur capable de détourer (WebGL + insertable streams ou repli canvas). */
export async function videoBackgroundSupported(): Promise<boolean> {
  try {
    return (await loadLib()).supportsBackgroundProcessors();
  } catch {
    return false;
  }
}

/** Effet voulu d'après le réglage, null = aucun. */
function wantedEffect(): { key: string; options: SwitchBackgroundProcessorOptions } | null {
  const raw = useGameStore.getState().settings.videoBackground;
  const value = isVideoBackground(raw) ? raw : 'none';
  if (value === 'blur') return { key: value, options: { mode: 'background-blur', blurRadius: BLUR_RADIUS } };
  if (value === 'blur-light') return { key: value, options: { mode: 'background-blur', blurRadius: LIGHT_BLUR_RADIUS } };
  if (value === 'custom') {
    const image = loadCustomBackground();
    // Image perso effacée (autre onglet, données du site vidées) : pas d'effet.
    return image ? { key: `custom:${image.length}:${image.slice(-32)}`, options: { mode: 'virtual-background', imagePath: image } } : null;
  }
  if (value.startsWith('photo:')) {
    const url = backgroundPhotoUrl(value.slice('photo:'.length));
    return { key: url, options: { mode: 'virtual-background', imagePath: url } };
  }
  return null;
}

/** Applique l'effet du réglage à la caméra publiée dans cette room. */
export async function syncVideoBackground(room: Room): Promise<void> {
  const track = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track as LocalVideoTrack | undefined;
  if (track) await syncTrackBackground(track);
}

/** Même chose sur une piste caméra quelconque (aperçu de l'écran d'accueil). */
export async function syncTrackBackground(track: LocalVideoTrack): Promise<void> {
  const wanted = wantedEffect();
  try {
    if (!wanted) {
      applied.delete(track);
      if (track.getProcessor()) await track.stopProcessor();
      return;
    }
    if (applied.get(track) === wanted.key && track.getProcessor()) return;
    const { BackgroundProcessor, supportsBackgroundProcessors } = await loadLib();
    if (!supportsBackgroundProcessors()) {
      if (!unsupportedWarned) {
        unsupportedWarned = true;
        EventBus.emit('ui:toast', { message: 'Ton navigateur ne permet pas de changer l’arrière-plan de la caméra.', tone: 'warning' });
      }
      return;
    }
    // Réglage changé pendant le chargement : la synchronisation suivante s'en charge.
    if (wantedEffect()?.key !== wanted.key) return;
    const current = track.getProcessor() as BackgroundProcessorWrapper | undefined;
    if (current && typeof current.switchTo === 'function') {
      // Passer d'un effet à l'autre sans recréer le traitement (pas de saut d'image).
      await current.switchTo(wanted.options);
    } else {
      if (current) await track.stopProcessor();
      await track.setProcessor(BackgroundProcessor(wanted.options));
    }
    applied.set(track, wanted.key);
  } catch (err) {
    console.warn('[videoBackground] échec:', err);
    EventBus.emit('ui:toast', { message: 'L’arrière-plan de la caméra n’a pas pu être appliqué.', tone: 'warning' });
  }
}
